import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Cargo } from '@eleg360/types';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';

/**
 * Cargo do mandato e a unidade territorial que ele implica.
 *
 * Um vereador trabalha por bairro; um deputado, por município; um presidente,
 * por estado. São as mesmas telas sobre outra camada do território — o que muda
 * é a palavra que nomeia a unidade.
 *
 * A unidade é derivada do cargo, não guardada em separado. Antes havia
 * `cargo` (texto livre) e `escala_padrao` (enum) convivendo, e nada garantia
 * que concordassem.
 */
export interface DefCargo {
  /** Rótulo completo, como aparece no topo e no cadastro. */
  rotulo: string;
  /** Unidade territorial no singular, para rótulos de campo e coluna. */
  unidade: string;
  /** Unidade no plural minúsculo, para frases como "todos os bairros". */
  plural: string;
}

/**
 * A unidade de cada cargo é a menor camada em que o dado eleitoral existe para
 * ele. Município e estado vêm do TSE; bairro não existe nos dados públicos e é
 * cadastrado pelo próprio gabinete.
 */
export const CARGOS: Record<Cargo, DefCargo> = {
  vereador: { rotulo: 'Vereador · bairro', unidade: 'Bairro', plural: 'bairros' },
  prefeito: {
    rotulo: 'Prefeito · bairro/distrito',
    unidade: 'Bairro/Distrito',
    plural: 'bairros e distritos',
  },
  deputado_estadual: {
    rotulo: 'Deputado estadual · município',
    unidade: 'Município',
    plural: 'municípios',
  },
  deputado_federal: {
    rotulo: 'Deputado federal · município',
    unidade: 'Município',
    plural: 'municípios',
  },
  governador: { rotulo: 'Governador · município', unidade: 'Município', plural: 'municípios' },
  senador: { rotulo: 'Senador · município', unidade: 'Município', plural: 'municípios' },
  presidente: { rotulo: 'Presidente · estado', unidade: 'Estado', plural: 'estados' },
};

export const CARGO_PADRAO: Cargo = 'vereador';

export const ehCargo = (v: unknown): v is Cargo => typeof v === 'string' && v in CARGOS;

/**
 * Unidade territorial do mandato logado.
 *
 * Enquanto o mandato não carregou, devolve a de vereador em vez de um vazio: a
 * alternativa seria piscar rótulos em branco em toda tela que tem coluna de
 * território.
 */
export function useEscala(): DefCargo & { cargo: Cargo } {
  const token = useAuthStore((s) => s.token) || '';

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
    staleTime: 60_000,
  });

  const cargo = ehCargo(mandato?.cargo) ? mandato.cargo : CARGO_PADRAO;
  return { cargo, ...CARGOS[cargo] };
}

/**
 * Grava o cargo do mandato no servidor e invalida o cache de `['mandato']`
 * (a fonte que `useEscala` lê) para que toda tela reflita a troca.
 *
 * A escala renomeia a unidade territorial em todo o sistema, então grava
 * direto: um botão "salvar" separado só criaria estado pela metade. Usado
 * tanto pelo seletor do topbar (`SeletorEscala`, troca imediata como o
 * protótipo) quanto pelo formulário de Configurações — uma única mutação
 * para uma mudança que afeta o sistema inteiro.
 */
export function useSalvarCargo() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cargo: Cargo) => adminApi.updateMandato({ cargo }, token),
    onSuccess: (_, cargo) => {
      queryClient.invalidateQueries({ queryKey: ['mandato'] });
      toast(
        `Cargo do mandato: ${CARGOS[cargo].rotulo} · unidade territorial passa a ser "${CARGOS[cargo].unidade}".`,
      );
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast(Array.isArray(msg) ? msg.join(' · ') : msg || 'Erro ao alterar o cargo do mandato.');
    },
  });
}
